import type { BuyerOption, OptionStateMap } from '@openx/types/options.ts';
import { createThirdPartySeatLookup } from '@openx/utils/apiHooks/utils/thirdPartySeats.ts';
import { useMemo } from 'react';
import { useAuth } from '../../../../components/AuthContext/context.ts';
import { type OptionsByPathQuery, useOptionsByPathQuery } from '../../../../hooks/useOptionsByPathQuery.ts';
import { isInternalUser } from '../../../../permissions/validators.ts';

type BuyerOptionByID = OptionStateMap<BuyerOption>['mapById'];
const EMPTY_OPTIONS_BY_PATH: OptionsByPathQuery['optionsByPath'] = [];

export const useBuyersFetch = (
  demandPartnerId?: string | null,
  onBehalfAccountId?: string | null,
  fetchAllWithoutDemandPartner = false
): OptionStateMap<BuyerOption> => {
  const { session } = useAuth();
  const showSeatName = isInternalUser(session);
  const shouldFetch = Boolean(demandPartnerId) || fetchAllWithoutDemandPartner;
  const { data: { optionsByPath = EMPTY_OPTIONS_BY_PATH } = {}, loading } = useOptionsByPathQuery({
    skip: !shouldFetch,
    variables: {
      filter: {
        ...(demandPartnerId && { demand_partner_id: demandPartnerId }),
        ...(onBehalfAccountId && { account_uid: onBehalfAccountId }),
      },
      path: 'deal.deal_participants.buyer_ids',
    },
  });

  const buyersMap = useMemo(
    () =>
      optionsByPath.reduce((acc, option) => {
        if (!option) return acc;

        const { id, name, extra: { third_party_buyer_id = undefined, third_party_seat = undefined } = {} } = option;

        if (third_party_buyer_id?.length > 0) {
          const findSeat = createThirdPartySeatLookup(third_party_seat, demandPartnerId);

          third_party_buyer_id.forEach(seatId => {
            const matchedSeat = findSeat(seatId);

            acc[seatId] = {
              buyer_id: id,
              id: seatId,
              name: showSeatName ? `${name} > ${seatId}` : seatId,
              seat_id: seatId,
              platform_seat_name:
                showSeatName && matchedSeat?.platform_seat_name
                  ? `${matchedSeat.platform_seat_name} > ${seatId}`
                  : seatId,
            };
          });
        } else {
          acc[id] = { buyer_id: id, id: id, name: name };
        }

        return acc;
      }, {} as BuyerOptionByID),
    [optionsByPath, demandPartnerId, showSeatName]
  );

  return { loading, mapById: buyersMap };
};
